/* eslint-disable no-script-url */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React from "react";
import {
  Box,
  IconButton,
  Typography,
  Grid,
  Chip,
  Divider
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';

const NotificationsList = () => {
  const navigate = useNavigate();

  // Updated mock data to match the screenshot
  const rows = [
    {
      id: 1,
      title: "Valentine's Day Collection",
      status: "Draft",
      description: "Discover our exclusive Valentine's Day collection. Perfect gifts for your loved ones!",
      recipients: 0,
      channels: ["mobile", "email"],
      date: "",
      stats: null
    },
    {
      id: 2,
      title: "Free Shipping Weekend",
      status: "Scheduled",
      description: "Enjoy free shipping on all orders this weekend! No minimum purchase required.",
      recipients: 8500,
      channels: ["email"],
      date: "12/23/2025, 11:01:25 PM",
      stats: null
    },
    {
      id: 3,
      title: "New Year Flash Sale",
      status: "Sent",
      description: "24-hour flash sale! Everything 30% off. Today only!",
      recipients: 18456,
      channels: ["mobile", "email"],
      date: "12/18/2025, 11:01:25 PM",
      stats: {
        delivered: "17,823",
        opened: { val: "6,234", rate: "33.8%" },
        clicked: { val: "2,891", rate: "46.4%" },
        failed: "633",
        unsubscribed: "0"
      }
    }
  ];

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'Draft':
        return (
          <Chip
            icon={<EditOutlinedIcon style={{ fontSize: 14 }} />}
            label="Draft"
            size="small"
            variant="outlined"
            sx={{
              borderColor: '#e0e0e0',
              color: '#757575',
              height: 24,
              fontSize: 12,
              ml: 1,
              '& .MuiChip-icon': { color: '#757575' }
            }}
          />
        );
      case 'Scheduled':
        return (
          <Chip
            icon={<AccessTimeIcon style={{ fontSize: 14 }} />}
            label="Scheduled"
            size="small"
            variant="outlined"
            sx={{
              borderColor: 'transparent',
              color: '#1976d2',
              bgcolor: '#e3f2fd',
              height: 24,
              fontSize: 12,
              ml: 1,
              '& .MuiChip-icon': { color: '#1976d2' }
            }}
          />
        );
      case 'Sent':
        return (
          <Chip
            icon={<CheckCircleOutlineIcon style={{ fontSize: 14 }} />}
            label="Sent"
            size="small"
            variant="outlined"
            sx={{
              borderColor: 'transparent',
              color: '#2e7d32',
              bgcolor: '#e8f5e9',
              height: 24,
              fontSize: 12,
              ml: 1,
              '& .MuiChip-icon': { color: '#2e7d32' }
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
      {rows.map((row) => (
        <Box
          key={row.id}
          sx={{
            bgcolor: '#fff',
            borderRadius: 2,
            border: '1px solid #e0e0e0',
            p: 2,
            boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
          }}
        >
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                {row.title}
              </Typography>
              {getStatusChip(row.status)}
            </Box>
            <Box>
              <IconButton size="small" onClick={() => navigate('/manage-notifications/detail')}>
                <VisibilityOutlinedIcon fontSize="small" sx={{ color: '#757575' }} />
              </IconButton>
              {row.status === 'Draft' && (
                <>
                  <IconButton size="small">
                    <EditOutlinedIcon fontSize="small" sx={{ color: '#757575' }} />
                  </IconButton>
                  <IconButton size="small">
                    <DeleteOutlineIcon fontSize="small" sx={{ color: '#757575' }} />
                  </IconButton>
                </>
              )}
              {row.status === 'Scheduled' && (
                <IconButton size="small">
                  <CloseIcon fontSize="small" sx={{ color: '#757575' }} />
                </IconButton>
              )}
            </Box>
          </Box>

          {/* Description */}
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {row.description}
          </Typography>

          {/* Info Row */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 3, mb: row.stats ? 2 : 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#666' }}>
              <PersonOutlineIcon fontSize="small" sx={{ fontSize: 18 }} />
              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                {row.recipients.toLocaleString()} recipients
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#666' }}>
              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>Channels:</Typography>
              {row.channels.includes('mobile') && <SmartphoneIcon fontSize="small" sx={{ fontSize: 18 }} />}
              {row.channels.includes('email') && <EmailOutlinedIcon fontSize="small" sx={{ fontSize: 18 }} />}
            </Box>

            {row.date && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#666' }}>
                <CalendarTodayOutlinedIcon fontSize="small" sx={{ fontSize: 18 }} />
                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                  {row.date}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Stats Footer for Sent items */}
          {row.stats && (
            <>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={4}>
                <Grid item>
                  <Typography variant="caption" color="textSecondary" display="block">Delivered</Typography>
                  <Typography variant="subtitle1" fontWeight="bold">{row.stats.delivered}</Typography>
                </Grid>
                <Grid item>
                  <Typography variant="caption" color="textSecondary" display="block">Opened</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">{row.stats.opened.val}</Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#4caf50',
                        bgcolor: '#e8f5e9',
                        px: 0.5,
                        py: 0.2,
                        borderRadius: 1,
                        fontWeight: 500
                      }}
                    >
                      {row.stats.opened.rate}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item>
                  <Typography variant="caption" color="textSecondary" display="block">Clicked</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">{row.stats.clicked.val}</Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#2196f3',
                        bgcolor: '#e3f2fd',
                        px: 0.5,
                        py: 0.2,
                        borderRadius: 1,
                        fontWeight: 500
                      }}
                    >
                      {row.stats.clicked.rate}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item>
                  <Typography variant="caption" color="textSecondary" display="block">Failed</Typography>
                  <Typography variant="subtitle1" fontWeight="bold" color="error">{row.stats.failed}</Typography>
                </Grid>
                <Grid item>
                  <Typography variant="caption" color="textSecondary" display="block">Unsubscribed</Typography>
                  <Typography variant="subtitle1" fontWeight="bold">{row.stats.unsubscribed}</Typography>
                </Grid>
              </Grid>
            </>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default NotificationsList;
